import { IFaucetConfig } from '../../common/FaucetConfig';
import { FaucetConfigContext, FaucetPageContext } from '../FaucetPage';
import React, { useContext } from 'react';
import { useParams, useNavigate, NavigateFunction } from "react-router-dom";
import { IFaucetContext } from '../../common/FaucetContext';
import { FaucetSession, IFaucetSessionStatus } from '../../common/FaucetSession';
import { toReadableAmount } from '../../utils/ConvertHelpers';
import { renderDate, renderTimespan } from '../../utils/DateUtils';
import { ClaimInput } from './ClaimInput';
import { OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { ClaimNotificationClient, IClaimNotificationUpdateData } from './ClaimNotificationClient';

import './ClaimPage.css'

export interface IClaimPageProps {
  pageContext: IFaucetContext;
  faucetConfig: IFaucetConfig;
  navigateFn: NavigateFunction;
  sessionId: string;
}

export interface IClaimPageState {
  sessionStatus: IFaucetSessionStatus;
  sessionDetails: {data: any, claim: any};
  loadingStatus: boolean;
  loadingError: string|boolean;
  isTimedOut: boolean;
  claimProcessing: boolean;
  refreshIndex: number;
  claimNotification: IClaimNotificationUpdateData;
  claimNotificationConnected: boolean;
}


export class ClaimPage extends React.PureComponent<IClaimPageProps, IClaimPageState> {
  private updateTimer: NodeJS.Timeout;
  private loadingStatus: boolean;
  private isTimedOut: boolean;
  private notificationClient: ClaimNotificationClient;
  private notificationClientActive: boolean;
  private lastStatusPoll: number;

  constructor(props: IClaimPageProps) {
    super(props);

    let claimWsEndpoint: string;
    if(this.props.pageContext.faucetUrls.wsBaseUrl) 
      claimWsEndpoint = this.props.pageContext.faucetUrls.wsBaseUrl + "/claim";
    else
      claimWsEndpoint = "/ws/claim";
    if(claimWsEndpoint.match(/^\//))
      claimWsEndpoint = location.origin.replace(/^http/, "ws") + claimWsEndpoint;
    this.notificationClient = new ClaimNotificationClient({
      claimWsUrl: claimWsEndpoint,
      sessionId: this.props.sessionId,
    });
    this.notificationClient.on("update", (message) => {
      this.setState({
        claimNotification: message.data,
      });
    });
    this.notificationClient.on("open", () => {
      this.setState({
        claimNotificationConnected: true,
      });
    });
    this.notificationClient.on("close", () => {
      this.setState({
        claimNotificationConnected: false,
      });
    });

    this.state = {
      sessionStatus: null,
      sessionDetails: null,
      loadingStatus: false,
      loadingError: false,
      isTimedOut: false,
      claimProcessing: false,
      refreshIndex: 0,
      claimNotification: null,
      claimNotificationConnected: false,
		};
  }

  public componentDidMount() {
    this.refreshSessionStatus();
  }

  public componentWillUnmount() {
    if(this.updateTimer) {
      clearTimeout(this.updateTimer);
      this.updateTimer = null;
    }
    if(this.notificationClientActive) {
      this.notificationClientActive = false;
      this.notificationClient.stop();
    }
  }

  private async refreshSessionStatus() {
    if(this.loadingStatus)
      return;
    
    this.loadingStatus = true;
    this.setState({
      loadingStatus: true,
    });

    try {
      let sessionStatus = await this.props.pageContext.faucetApi.getSessionStatus(this.props.sessionId, !this.state.sessionDetails);
      if(sessionStatus.details) {
        this.setState({
          sessionDetails: sessionStatus.details,
        })
      }
      this.setState({
        loadingStatus: false,
        sessionStatus: sessionStatus,
      }, () => {
        this.setUpdateTimer();
      });
    }
    catch(err) {
      this.setState({
        loadingStatus: false,
        loadingError: err.error?.toString() || err.toString() || true,
      });
    }
    this.loadingStatus = false;
  }

  private setUpdateTimer() {
    if(this.updateTimer) {
      clearTimeout(this.updateTimer);
      this.updateTimer = null;
    }
    let exactNow = (new Date()).getTime();

    let timeLeft = (1000 - (exactNow % 1000)) + 2;
    this.updateTimer = setTimeout(() => {
      this.updateTimer = null;
      this.setState({
        refreshIndex: this.state.refreshIndex + 1,
      });
      this.setUpdateTimer();
    }, timeLeft);
  }

	public render(): React.ReactElement<IClaimPageProps> {
    let exactNow = (new Date()).getTime();
    let now = this.props.pageContext.faucetApi.getFaucetTime().getSyncedTime();

    if(this.state.sessionStatus) {
      let claimTimeout = (this.state.sessionStatus.start + this.props.faucetConfig.sessionTimeout) - now;
      if(claimTimeout < 0 && this.state.sessionStatus.status === "claimable" && !this.isTimedOut) {
        this.isTimedOut = true;
        this.setState({
          isTimedOut: true
        });
        
        this.props.pageContext.showDialog({
          title: "Claim expired",
          body: (
            <div className='alert alert-danger'>
              Sorry, your reward ({toReadableAmount(BigInt(this.state.sessionStatus.balance), this.props.faucetConfig.faucetCoinDecimals, this.props.faucetConfig.faucetCoinSymbol)}) has not been claimed in time.
            </div>
          ),
          closeButton: {
            caption: "Close"
          },
          closeFn: () => {
            this.refreshSessionStatus();
          }
        });
      }

      if(this.state.sessionStatus.status === "claiming") {
        if(!this.notificationClientActive) {
          this.notificationClientActive = true;
          this.notificationClient.start();
        }

        if(exactNow - this.lastStatusPoll > 30 * 1000 || this.state.sessionStatus.claimIdx <= (this.state.claimNotification?.confirmedIdx || 0)) {
          this.lastStatusPoll = exactNow;
          this.refreshSessionStatus();
        }
      }
      else {
        if(this.notificationClientActive) {
          this.notificationClientActive = false;
          this.notificationClient.stop();
        }
      }
    }

    let claimStep = this.getClaimStep();

    return (
      <div className='page-claim'>
        <div className='container'>
          {this.renderClaim(claimStep)}
        </div>
      </div>
    )
	}

  private getClaimStep(): number {
    if(!this.state.sessionStatus) return 0;
    switch(this.state.sessionStatus.status) {
      case "claimable": return 1;
      case "claiming": return 2;
      case "finished": return 3;
      case "failed": return -1;
      default: return 0;
    }
  }

  private renderStepper(step: number): React.ReactElement {
    let labels = ["Ready", "Processing", "Complete"];
    return (
      <div className='claim-stepper'>
        {labels.map((label, i) => {
          let stepNum = i + 1;
          let isActive = step >= stepNum;
          let isCurrent = step === stepNum;
          let isFailed = step === -1;
          return (
            <React.Fragment key={i}>
              {i > 0 && <div className={'claim-stepper-line' + (step > stepNum - 1 ? ' active' : '') + (isFailed ? ' failed' : '')}></div>}
              <div className={'claim-stepper-step' + (isActive && !isFailed ? ' active' : '') + (isCurrent && !isFailed ? ' current' : '') + (isFailed && i === 0 ? ' active' : '') + (isFailed && i > 0 ? ' failed' : '')}>
                <div className='claim-stepper-dot'>
                  {(isActive && !isCurrent && !isFailed) || (step === 3) ?
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 5.5L3.5 8L9 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/></svg>
                  : (isFailed && i > 0) ?
                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1 1L7 7M7 1L1 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/></svg>
                  :
                    <span>{stepNum}</span>
                  }
                </div>
                <span className='claim-stepper-label'>{isFailed && i === 1 ? 'Failed' : label}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  private renderClaim(step: number): React.ReactElement {
    if(this.state.loadingError) {
      return (
        <div className='error-panel'>
          <div className='error-panel-message'>No claimable reward found: {typeof this.state.loadingError == "string" ? this.state.loadingError : ""}</div>
        </div>
      );
    }
    else if(!this.state.sessionStatus) {
      return (
        <div className="faucet-loading">
          <div className="loading-spinner">
            <img src={(this.props.pageContext.faucetUrls.imagesUrl || "/images") + "/spinner.gif"} className="spinner" />
            <span className="spinner-text">Loading Claim...</span>
          </div>
        </div>
      );
    }
    else if(this.state.isTimedOut) {
      return (
        <div className='error-panel'>
          <div className='error-panel-message'>Sorry, your reward ({toReadableAmount(BigInt(this.state.sessionStatus.balance), this.props.faucetConfig.faucetCoinDecimals, this.props.faucetConfig.faucetCoinSymbol)}) has not been claimed in time.</div>
        </div>
      );
    }

    let now = this.props.pageContext.faucetApi.getFaucetTime().getSyncedTime();
    let claimTimeout = (this.state.sessionStatus.start + this.props.faucetConfig.sessionTimeout) - now;
    let amount = toReadableAmount(BigInt(this.state.sessionStatus.balance), this.props.faucetConfig.faucetCoinDecimals, this.props.faucetConfig.faucetCoinSymbol);

    return (
      <div className='claim-flow'>
        {this.renderStepper(step)}

        <div className='claim-summary'>
          <div className='claim-summary-amount'>{amount}</div>
          <div className='claim-summary-addr'>{this.state.sessionStatus.target}</div>
          {this.state.sessionStatus.status === "claimable" ?
            <div className='claim-summary-timeout'>
              Expires {renderTimespan(claimTimeout)}
            </div>
          : null}
        </div>

        {this.state.sessionStatus.status === "claimable" ? this.renderClaimForm() : null}
        {this.state.sessionStatus.status === "claiming" ? this.renderClaimStatus() : null}
        {this.state.sessionStatus.status === "failed" ? this.renderSessionFailed() : null}
        {this.state.sessionStatus.status === "finished" ? this.renderSessionFinished() : null}
        {this.state.sessionStatus.status !== "claimable" ?
          <button
            className="btn btn-secondary claim-back-btn"
            onClick={() => this.props.navigateFn("/")}>
            Back to faucet
          </button>
        : null}
      </div>
    );
  }

  private renderClaimForm(): React.ReactElement {
    return (
      <div className='claim-form'>
        <ClaimInput
          faucetConfig={this.props.faucetConfig}
          submitInputs={(claimData) => this.submitClaim(claimData)}
        />
      </div>
    );
  }

  private renderClaimStatus(): React.ReactElement {
    let isSending = this.state.sessionStatus.claimHash || this.state.sessionStatus.claimIdx <= this.state.claimNotification?.processedIdx;
    return (
      <div className='claim-step-content claim-processing'>
        <div className='processing-indicator'>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Processing...</span>
          </Spinner>
        </div>
        <p className="processing-hint">You can close this page. Your claim will be processed automatically.</p>
        <div className="processing-details">
          <div className='processing-row'>
            <span className='processing-label'>Status</span>
            <span className={'processing-value processing-status-badge' + (isSending ? ' sending' : '')}>
              {isSending ? "Sending" : "Queued"}
            </span>
          </div>
          {this.state.sessionStatus.claimHash ?
            <div className='processing-row'>
              <span className='processing-label'>TX Hash</span>
              <span className='processing-value processing-hash'>
                {this.props.faucetConfig.ethTxExplorerLink ?
                  <a href={this.props.faucetConfig.ethTxExplorerLink.replace("{txid}", this.state.sessionStatus.claimHash)} target='_blank' rel='noopener noreferrer'>{this.state.sessionStatus.claimHash}</a> :
                  <span>{this.state.sessionStatus.claimHash}</span>}
              </span>
            </div>
          : null}
          <div className='processing-row'>
            <span className='processing-label'>Queue</span>
            <span className='processing-value'>
              <OverlayTrigger
                placement="auto"
                container={this.props.pageContext.getContainer()}
                overlay={
                  <Tooltip>
                    {((this.state.sessionStatus.claimIdx || 0) - (this.state.claimNotification?.processedIdx || 0) - 1)} claims will be processed before yours.
                  </Tooltip>
                }
              >
                <span>
                  #{(this.state.sessionStatus.claimHash || (this.state.sessionStatus.claimIdx || 0) <= (this.state.claimNotification?.processedIdx || 0)) ?
                    "0" :
                    (this.state.sessionStatus.claimIdx || 0) - (this.state.claimNotification?.processedIdx || 0)
                  }
                </span>
              </OverlayTrigger>
            </span>
          </div>
        </div>
      </div>
    );
  }

  private renderSessionFailed(): React.ReactElement {
    return (
      <div className='claim-step-content'>
        <div className='error-panel'>
          <div className='error-panel-message'>{this.state.sessionStatus.failedReason || this.state.sessionStatus.claimMessage || "Claim failed"}</div>
        </div>
      </div>
    )
  }

  private renderSessionFinished(): React.ReactElement {
    let amount = toReadableAmount(BigInt(this.state.sessionStatus.balance), this.props.faucetConfig.faucetCoinDecimals, this.props.faucetConfig.faucetCoinSymbol);
    return (
      <div className='claim-step-content claim-success'>
        <div className='success-header'>
          <div className='success-icon'>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M4 12.5L9.5 18L20 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square"/></svg>
          </div>
          <div className='success-title'>Tokens Sent!</div>
          <div className='success-amount'>{amount}</div>
        </div>
        <div className='success-details'>
          <div className='success-row'>
            <span className='success-label'>Block</span>
            <span className='success-value'>#{this.state.sessionStatus.claimBlock}</span>
          </div>
          <div className='success-row'>
            <span className='success-label'>TX Hash</span>
            <span className='success-value success-hash'>
              {this.props.faucetConfig.ethTxExplorerLink ?
                <a href={this.props.faucetConfig.ethTxExplorerLink.replace("{txid}", this.state.sessionStatus.claimHash)} target='_blank' rel='noopener noreferrer'>{this.state.sessionStatus.claimHash}</a> :
                <span>{this.state.sessionStatus.claimHash}</span>}
            </span>
          </div>
        </div>
        {this.renderResultSharing()}
      </div>
    );
  }

  private renderResultSharing(): React.ReactElement {
    let shareEls: React.ReactElement[] = [];

    if(this.props.faucetConfig.resultSharing?.twitter) {
      let tweetMsg = this.replaceShareMessagePlaceholders(this.props.faucetConfig.resultSharing.twitter);
      let tweetUrl = "https://twitter.com/intent/tweet?text=" + encodeURIComponent(tweetMsg);
      shareEls.push(
        <span key='tw' className='sh-link sh-tw'>
          <a href='#' target='_blank' data-url={tweetUrl} rel='noopener noreferrer' onClick={function(evt) {
            let a = document.createElement('a');
            a.target = '_blank';
            a.href = tweetUrl;
            a.click();

            evt.preventDefault();
          }}><i /><span>Tweet</span></a>
        </span>
      );
    }
    if(this.props.faucetConfig.resultSharing?.mastodon) {
      let tweetMsg = this.replaceShareMessagePlaceholders(this.props.faucetConfig.resultSharing.mastodon);

      let tweetUrl = "/share?text=" + encodeURIComponent(tweetMsg);
      shareEls.push(
        <span  key='md' className='sh-link sh-md'>
          <a href={'https://mastodon.social' + tweetUrl} target='_blank' data-url={tweetUrl} rel='noopener noreferrer' onClick={function(evt) {

            var mastodonUrl = evt.currentTarget.getAttribute("data-instance");
            if(!mastodonUrl)
              mastodonUrl = prompt("Please enter the URL of the mastodon instance you'd like to share to:", "https://mastodon.social");
            if(mastodonUrl) {
              evt.currentTarget.setAttribute("href", mastodonUrl.replace(/\/$/, "") + tweetUrl);
              evt.currentTarget.setAttribute("data-instance", mastodonUrl);
            }
            else
              evt.preventDefault();
          }}><i /><span>Post</span></a>
        </span>
      );
    }

    let resultSharingCaption = this.props.faucetConfig.resultSharing.caption || "Support this faucet with a ";
    return (
      <div className='result-sharing'>
        {this.props.faucetConfig.resultSharing.preHtml ?
          <div className="sh-html" dangerouslySetInnerHTML={{__html: this.replaceShareMessagePlaceholders(this.props.faucetConfig.resultSharing.preHtml)}} />
        : null}
        {shareEls.length > 0 ? 
          <div className='sh-opt'>
            <span className='sh-label'>{resultSharingCaption}</span>
            {shareEls}
          </div>
        : null}
        {this.props.faucetConfig.resultSharing.postHtml ?
          <div className="sh-html" dangerouslySetInnerHTML={{__html: this.replaceShareMessagePlaceholders(this.props.faucetConfig.resultSharing.postHtml)}} />
        : null}
      </div>
    )
  }

  private replaceShareMessagePlaceholders(message: string): string {
    message = message.replace(/{sessionid}/ig, this.state.sessionStatus.session);
    message = message.replace(/{target}/ig, this.state.sessionStatus.target);
    message = message.replace(/{token}/ig, this.props.faucetConfig.faucetCoinSymbol);

    message = message.replace(/{amount}/ig, toReadableAmount(BigInt(this.state.sessionStatus.balance), this.props.faucetConfig.faucetCoinDecimals));
    let safeUrl = location.protocol + "//" + location.hostname + location.pathname;
    message = message.replace(/{url}/ig, safeUrl);

    let claimableTime = this.state.sessionDetails.data['close.time'] || this.props.pageContext.faucetApi.getFaucetTime().getSyncedTime();
    let duration = claimableTime - this.state.sessionStatus.start;
    message = message.replace(/{duration}/ig, renderTimespan(duration));

    message = message.replace(/{hashrate}/ig, () => {
      let hashrate = duration > 0 ? (this.state.sessionDetails.data['pow.lastNonce'] || 0) / duration : 0;
      return (Math.round(hashrate * 100) / 100).toString()
    });

    return message;
  }

  private async submitClaim(claimData: any): Promise<void> {
    try {
      claimData = Object.assign({
        session: this.props.sessionId
      }, claimData ||{});

      let sessionStatus = await this.props.pageContext.faucetApi.claimReward(claimData);
      if(sessionStatus.status === "failed")
        throw sessionStatus;
      
      this.lastStatusPoll = new Date().getTime();
      this.setState({
        sessionStatus: sessionStatus,
      });
      FaucetSession.persistSessionInfo(null);
    } catch(ex) {
      let errorBody: React.ReactElement;
      if(ex && ex.failedCode) {
        errorBody = (
          <div className='error-panel'>
            <div className='error-panel-message'>{ex.failedReason || "Could not claim rewards"}</div>
          </div>
        );
      } else {
        errorBody = (
          <div className='error-panel'>
            <div className='error-panel-message'>Could not claim rewards: {ex.toString()}</div>
          </div>
        );
      }
      this.props.pageContext.showDialog({
        title: "Claim failed",
        body: errorBody,
        closeButton: {
          caption: "Close"
        }
      });
      throw ex;
    }
  }

}

export default (props) => {
  let params = useParams();
  return (
    <ClaimPage 
      key={params.session}
      {...props}
      pageContext={useContext(FaucetPageContext)}
      faucetConfig={useContext(FaucetConfigContext)}
      navigateFn={useNavigate()}
      sessionId={params.session}
    />
  );
};

